import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, RefreshCw, Trash2, Bell, BellOff } from "lucide-react";
import { useState } from "react";

const ResetTab = ({
   searchTerm,
   setSearchTerm,
   filteredApplications,
   applicationNumbersLoading,
   isResettingSpecific,
   handleResetSpecific,
   setShowResetAllDialog,
   setShowUnsubmitAllDialog,
   handleResetDatabase,
   handleDeleteAllApplications,
   handleDeleteAllUserNotifications,
   handleDeleteAllPersonnelNotifications
}) => {
   const [showResetDatabaseDialog, setShowResetDatabaseDialog] = useState(false);
   const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
   const [showDeleteUserNotificationsDialog, setShowDeleteUserNotificationsDialog] = useState(false);
   const [showDeletePersonnelNotificationsDialog, setShowDeletePersonnelNotificationsDialog] = useState(false);

   return (
      <Card>
         <CardHeader>
            <CardTitle>Reset Options</CardTitle>
            <CardDescription>Reset application states and data</CardDescription>
         </CardHeader>
         <CardContent className="space-y-4">
            {/* Delete All Applications */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Delete All Applications</Label>
                     <p className="text-sm text-muted-foreground">
                        Delete all applications and related records. This action cannot be undone.
                     </p>
                  </div>
                  <Button
                     variant="destructive"
                     onClick={() => setShowDeleteAllDialog(true)}
                     className="flex items-center gap-2"
                  >
                     <Trash2 className="h-4 w-4" />
                     Delete All
                  </Button>
               </div>
            </div>

            {/* Reset Database Section */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Reset Database</Label>
                     <p className="text-sm text-muted-foreground">
                        Reset the entire test database to a clean state. This action cannot be undone.
                     </p>
                  </div>
                  <Button
                     variant="destructive"
                     onClick={() => setShowResetDatabaseDialog(true)}
                  >
                     Reset Database
                  </Button>
               </div>
            </div>

            {/* Reset User Notifications */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Reset User Notifications</Label>
                     <p className="text-sm text-muted-foreground">
                        Delete all user notifications. This action cannot be undone.
                     </p>
                  </div>
                  <Button
                     variant="destructive"
                     onClick={() => setShowDeleteUserNotificationsDialog(true)}
                     className="flex items-center gap-2"
                  >
                     <Bell className="h-4 w-4" />
                     Delete All
                  </Button>
               </div>
            </div>

            {/* Reset Personnel Notifications */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Reset Personnel Notifications</Label>
                     <p className="text-sm text-muted-foreground">
                        Delete all personnel notifications. This action cannot be undone.
                     </p>
                  </div>
                  <Button
                     variant="destructive"
                     onClick={() => setShowDeletePersonnelNotificationsDialog(true)}
                     className="flex items-center gap-2"
                  >
                     <BellOff className="h-4 w-4" />
                     Delete All
                  </Button>
               </div>
            </div>

            {/* Reset All Applications */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Reset All Applications</Label>
                     <p className="text-sm text-muted-foreground">
                        Reset all applications to their initial state
                     </p>
                  </div>
                  <Button
                     variant="destructive"
                     onClick={() => setShowResetAllDialog(true)}
                  >
                     Reset All
                  </Button>
               </div>
            </div>

            {/* Reset Specific Application */}
            <div className="space-y-2">
               <Label>Reset Specific Application</Label>
               <div className="space-y-2">
                  <Input
                     placeholder="Search application number..."
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <ScrollArea className="h-[200px] rounded-md border">
                     <div className="p-4 space-y-2">
                        {applicationNumbersLoading ? (
                           <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="ml-2">Loading applications...</span>
                           </div>
                        ) : filteredApplications.length === 0 ? (
                           <p className="text-center text-sm text-muted-foreground py-4">
                              No applications found
                           </p>
                        ) : (
                           filteredApplications.map((appNumber) => (
                              <Button
                                 key={appNumber}
                                 variant="outline"
                                 className="w-full justify-between"
                                 onClick={() => handleResetSpecific(appNumber)}
                                 disabled={isResettingSpecific}
                              >
                                 {appNumber}
                                 <RefreshCw className="h-4 w-4 ml-2" />
                              </Button>
                           ))
                        )}
                     </div>
                  </ScrollArea>
               </div>
            </div>

            {/* Unsubmit All Applications */}
            <div className="space-y-2">
               <div className="flex justify-between items-start">
                  <div>
                     <Label>Unsubmit All Applications</Label>
                     <p className="text-sm text-muted-foreground">
                        Move all submitted applications back to draft
                     </p>
                  </div>
                  <Button
                     variant="outline"
                     className="text-yellow-600"
                     onClick={() => setShowUnsubmitAllDialog(true)}
                  >
                     Unsubmit All
                  </Button>
               </div>
            </div>

            {/* Reset Database Dialog */}
            <AlertDialog open={showResetDatabaseDialog} onOpenChange={setShowResetDatabaseDialog}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Reset Database</AlertDialogTitle>
                     <AlertDialogDescription>
                        This will permanently delete all applications, certificates, payments, and related data.
                        This action cannot be undone.
                     </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction
                        onClick={() => {
                           handleResetDatabase();
                           setShowResetDatabaseDialog(false);
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                     >
                        Reset Database
                     </AlertDialogAction>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>

            {/* Delete All Applications Dialog */}
            <AlertDialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Delete All Applications</AlertDialogTitle>
                     <AlertDialogDescription>
                        This will permanently delete all applications and their related records 
                        (certificates, payments, notifications, etc.). This action cannot be undone.
                     </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction
                        onClick={() => {
                           handleDeleteAllApplications();
                           setShowDeleteAllDialog(false);
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                     >
                        Delete All
                     </AlertDialogAction>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>

            {/* Delete User Notifications Dialog */}
            <AlertDialog open={showDeleteUserNotificationsDialog} onOpenChange={setShowDeleteUserNotificationsDialog}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Delete All User Notifications</AlertDialogTitle>
                     <AlertDialogDescription>
                        This will permanently delete all user notifications. This action cannot be undone.
                     </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction
                        onClick={() => {
                           handleDeleteAllUserNotifications();
                           setShowDeleteUserNotificationsDialog(false);
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                     >
                        Delete All
                     </AlertDialogAction>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>

            {/* Delete Personnel Notifications Dialog */}
            <AlertDialog open={showDeletePersonnelNotificationsDialog} onOpenChange={setShowDeletePersonnelNotificationsDialog}>
               <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Delete All Personnel Notifications</AlertDialogTitle>
                     <AlertDialogDescription>
                        This will permanently delete all personnel notifications. This action cannot be undone.
                     </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction
                        onClick={() => {
                           handleDeleteAllPersonnelNotifications();
                           setShowDeletePersonnelNotificationsDialog(false);
                        }}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                     >
                        Delete All
                     </AlertDialogAction>
                  </AlertDialogFooter>
               </AlertDialogContent>
            </AlertDialog>
         </CardContent>
      </Card>
   );
};

export default ResetTab;